import React, { useState, useEffect } from 'react';
import config from '../../config';
import Grid from '@mui/material/Grid';
import { translate } from '../../i18n/customI18nProvider';
import { Button } from '@mui/material';
import { Add } from '@mui/icons-material';
import { DndContext, closestCenter, useSensor, useSensors, MouseSensor, TouchSensor } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DepositReturnForm from './DepositReturnForm';
import { getBarPossibleDepositReturns, reorderBarPossibleDepositReturns } from '../../services/bar.service';
import { notifyError, notifySuccess } from '../../util';
import { useNotify } from 'react-admin';

const styles = {
    item: {
        container: {
            display: "flex",
            flex: 1,
            justifyContent: "space-between",
            border: "1px solid lightgray",
            borderRadius: "5px",
            padding: "10px",
            paddingTop: "20px",
            paddingBottom: "20px",
            userSelect: "none",
            WebkitUserSelect: "none",
            touchAction: "pan-y"
        },
        button: {
            textAlign: "center",
            alignContent: "center",
            background: "lightGray",
            borderRadius: "5px",
            height: "30px",
            width: "30px"
        }
    }
}

const DepositReturnList = ({ deposits, currencyCode, bar_id }) => {
    const [list, setList] = useState([]);
    const [form, setForm] = useState({ show: false, element: null });
    const notify = useNotify();
    
    
    const mouseSensor = useSensor(MouseSensor, {
      // Require the mouse to move by 10 pixels before activating
      activationConstraint: {
        distance: 10,
      },
    });
    const touchSensor = useSensor(TouchSensor, {
      // Press delay of 250ms, with tolerance of 5px of movement
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    });
    
    const sensors = useSensors(
      mouseSensor,
      touchSensor
    );
    
    const handleFormClose = () => {
        setForm({ show: false, element: null });
        fetchDeposits();
    }
    
    const handleDragStart = (event) => {
        if (window.navigator && window.navigator.vibrate) {
          window.navigator.vibrate(25);
        }
    };

    const fetchReturns = async () => {
        try {
            const returns = await getBarPossibleDepositReturns(bar_id);
            if (returns) {
                returns.sort((a, b) => a.index - b.index);
                setList(returns);
            }
        } catch (error) {
            console.error('Error fetching deposit returns:', error);
        }
    };

    useEffect(() => { fetchReturns(); }, []);

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const newList = arrayMove(
                list,
                list.findIndex(i => i === active.id),
                list.findIndex(i => i === over.id)
            );
            setList(newList);
            const response = await reorderBarPossibleDepositReturns(bar_id, newList);
            if (response.error) notifyError(response.error.message || response.error);
            else notifySuccess('custom.order_saved');
        }
    };
    
    const findDeposit = (id) => {
        let deposit = null;
        for (const element of deposits) {
            if (element._id === id) {
                deposit = element;
                break;
            }
        }
        return deposit;
    }
    
    const findColor = (colorId) => {
        const colorObj = config.consumption_colors.find((color) => color.id === colorId);
        return colorObj ? colorObj.colorCode : config.baseColorLight;
    };
    
    const formatPrice = (price) => {
        return `${currencyCode} -${price.toFixed(2)}`;
    }

    const SortableReturnItem = ({ item }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item._id });
        const style = {
            display: 'flex',
            transform: CSS.Transform.toString(transform),
            transition,
            cursor: isDragging ? 'grabbing' : 'grab',
            opacity: isDragging ? 0.5 : 1,
            zIndex: isDragging ? 100 : 1,
            marginBottom: "10px"
        };

        return (
            <div style={style} ref={setNodeRef} {...attributes} {...listeners}>
                <div onClick={() => setForm({ show: true, element: item })}  style={{ ...styles.item.container, background: findColor(item.return_color) }}>
                    <div>
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "5px" }}>
                            <h4 style={{ fontSize: "15px", color: config.baseColor, margin: "0px" }}>{`${translate('custom.return')} ${item.name}`}</h4>
                        </div>
                        <div style={{ display: "flex", alignItems: "center"}}>
                            <p style={{ margin: "0px", fontSize: "11px" }}>{formatPrice(item.price)}</p>
                        </div>
                    </div>
                    <div 
                        style={{ display: "flex", background: "white", padding: "5px", borderRadius: "5px", height: "30px", marginTop: "auto", marginBottom: "auto" }}
                    >
                        <div style={styles.item.button}>-</div>
                        <p style={{ margin: "0px", width: "30px", textAlign: "center", alignSelf: "center" }}>0</p>
                        <div style={styles.item.button}>+</div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div>
            <h3 style={{ marginBottom: "0px", borderBottom: '2px solid ' + config.baseColor, color: config.baseColor }}>
                {translate('custom.possible_deposit_returns')}
            </h3>
            <p style={{ fontSize: "14px", color: "gray", fontStyle: "italic", marginBottom: "20px", marginTop: "5px" }}> {translate('custom.possible_deposit_list_info')}</p>
            <div style={{ maxWidth: "800px", margin: "auto" }}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
                    <SortableContext items={list} strategy={verticalListSortingStrategy}>
                        {list.map(item => <SortableReturnItem key={item} item={findDeposit(item)} />)}
                    </SortableContext>
                </DndContext>
    
                {list.length < deposits.length && (
                    <div style={{ ...styles.item.container, justifyContent: "center", alignItems: "center", background: "white", cursor: "pointer" }} onClick={() => { setForm({ show: true, element: null }) }}>
                        <Button startIcon={<Add />}>
                            {translate('custom.add')}
                        </Button>
                    </div>  
                )}
            </div>

            <DepositReturnForm
                barId={bar_id}
                currencyCode={currencyCode}
                show={form.show}
                depositReturn={form.element}
                depositReturns={deposits}
                onClose={() => { setForm({ show: false, element: null }); fetchReturns(); }}
                selectedReturns={list}
            />
        </div>
    );
};

export default DepositReturnList;
